// frontend/src/widgets/QuoteStageProgressWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-28
// Updated:      2026-09-09 13:40 BST
//
// Description:
//   Quote status progress + governed status actions for Quotes page.
//   Mirrors OpportunityStageProgressWidget visuals (clip-path chevrons + portal tooltip)
//   but operates on quote.status lifecycle.
//
// Lifecycle (DB-enforced):
//   draft → submitted → approved | rejected → sent → accepted | expired | withdrawn
//
// Includes:
//   • Chevron progress bar (clip-path, deterministic gap)
//   • Stage tooltips (portal, multiline)
//   • Allowed-next-status buttons (from GET /api/quotes/{id}/status/meta) with tooltips
//
// IMPORTANT:
//   Uses shared Axios instance ONLY.
//   No inline error banners: errors should be emitted to global alerts by the caller (or future wiring).
//
// Mobile Responsiveness (2026-09-09):
//   • Desktop retains the existing left-to-right quote-status chevron bar.
//   • Mobile (<=767px) uses the same status elements in a compact top-to-bottom flow.
//   • Pane 1 shows the complete quote lifecycle in full.
//   • Native vertical scroll/snap moves naturally to Pane 2, which contains the
//     existing governed status-transition controls and editability state.
//   • No swipe instruction text, paging controls or horizontal carousel is used.
//   • Mobile stages are compact (44px high / 10.5px labels) because the quote
//     lifecycle has eight statuses and must remain visually light.
//   • Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary pager/root sizing;
//       measure both panes at natural height;
//       apply the larger shared pane height;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   • ResizeObserver watches both panes so later content/state changes recalculate.
//   • No quote loading, metadata loading, governed transition callback, payload,
//     status-state calculation or tooltip event wiring is changed.
//
// Operational Notes:
//   • One render tree is shared by desktop and mobile.
//   • Existing desktop/tablet behaviour is preserved at >=768px.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

const norm = (v) => String(v || "").trim().toLowerCase();

const titleCase = (s) =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const QUOTE_STATUSES = ["draft", "submitted", "approved", "rejected", "sent", "accepted", "expired", "withdrawn"];

const STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  approved: "Approved",
  rejected: "Rejected",
  sent: "Sent",
  accepted: "Accepted",
  expired: "Expired",
  withdrawn: "Withdrawn",
};

const STATUS_HELP = {
  draft: "Quote is being prepared and can be edited.",
  submitted: "Quote has been submitted for approval.",
  approved: "Quote has been approved and can be sent.",
  rejected: "Quote was rejected and needs amendment or closure.",
  sent: "Quote has been sent to the customer.",
  accepted: "Customer accepted the quote/order.",
  expired: "Quote expired without acceptance.",
  withdrawn: "Customer withdrew after interest/acceptance or pulled the order.",
};

const statusIndex = (s) => {
  const k = norm(s);
  const i = QUOTE_STATUSES.indexOf(k);
  return i >= 0 ? i : 0;
};

const statusLabel = (s) => STATUS_LABELS[norm(s)] || titleCase(s);

const buttonClassForStatus = (s) => {
  const k = norm(s);
  if (k === "rejected" || k === "expired" || k === "withdrawn") {
    return "bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white";
  }
  return "bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white";
};

// ---------- Gold-standard tooltip (label-hover, portal, multiline, no native title) ----------
function usePortalTooltip(theme) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const rafRef = useRef(null);

  const hide = useCallback(() => {
    setOpen(false);
    setText("");
  }, []);

  const onMove = useCallback((e) => {
    const x = e?.clientX || 0;
    const y = e?.clientY || 0;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setPos({ x, y }));
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && hide();
    const onScroll = () => hide();
    const onBlur = () => hide();

    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hide]);

  const tooltipNode = useMemo(() => {
    if (!open || !text) return null;

    const pad = 12;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;

    const left = Math.min(vw - pad, Math.max(pad, pos.x + 14));
    const top = Math.min(vh - pad, Math.max(pad, pos.y + 14));

    return createPortal(
      <div
        style={{
          position: "fixed",
          left,
          top,
          zIndex: 999999,
          pointerEvents: "none",
          background: theme?.primary_color || "#111827",
          color: "#fff",
          padding: "10px 12px",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: "16px",
          maxWidth: 360,
          whiteSpace: "pre-line",
        }}
      >
        {text}
      </div>,
      document.body
    );
  }, [open, pos.x, pos.y, text, theme]);

  const bind = useMemo(
    () => ({
      onMouseEnter: (t) => {
        setText(String(t || ""));
        setOpen(true);
      },
      onMouseLeave: () => hide(),
      onMouseMove: onMove,
    }),
    [hide, onMove]
  );

  return { tooltipNode, bind };
}

export default function QuoteStageProgressWidget({ quoteId, refreshKey, onQuoteChanged }) {
  const { theme } = useTheme();
  const tooltip = usePortalTooltip(theme);

  const widgetRootRef = useRef(null);
  const mobilePagerRef = useRef(null);
  const stagePaneRef = useRef(null);
  const actionsPaneRef = useRef(null);

  const [quote, setQuote] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!quoteId) {
      setQuote(null);
      setMeta(null);
      return;
    }

    setLoading(true);
    try {
      const [qRes, mRes] = await Promise.all([
        api.get(`/api/quotes/${quoteId}`),
        api.get(`/api/quotes/${quoteId}/status/meta`),
      ]);
      setQuote(qRes.data || null);
      setMeta(mRes.data || null);
    } catch {
      setQuote(null);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const currentStatus = norm(quote?.status || meta?.status);
  const currentIdx = statusIndex(currentStatus);

  const stateByIdx = useMemo(
    () => QUOTE_STATUSES.map((_, i) => (i < currentIdx ? "complete" : i === currentIdx ? "current" : "future")),
    [currentIdx]
  );

  const allowedNext = useMemo(() => {
    const arr = meta?.allowed_next_statuses;
    return Array.isArray(arr) ? arr.map((x) => norm(x)).filter(Boolean) : [];
  }, [meta]);

  const canEdit = Boolean(meta?.can_edit_quote);

  const doTransition = useCallback(
    async (toStatus) => {
      if (!quoteId) return;
      setBusy(true);
      try {
        await api.post(`/api/quotes/${quoteId}/status`, {
          status: String(toStatus),
          to_status: String(toStatus),
          next_status: String(toStatus),
          override: false,
        });
        await load();
        onQuoteChanged?.();
      } catch {
        // No inline error banner per spec.
      } finally {
        setBusy(false);
      }
    },
    [load, onQuoteChanged, quoteId]
  );

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minHeight: 0,
      ["--vx-primary"]: theme?.primary_color || "#0b2a3c",
      ["--vx-accent"]: theme?.accent_color || "#ff7a00",
      ["--vx-success"]: "#16a34a",
      ["--vx-danger"]: "#dc2626",
      ["--vx-warning"]: "#f59e0b",
      ["--vx-info"]: "#2563eb",
      ["--vx-muted"]: "#6b7280",
      ["--vx-barbg"]: "#F3F4F6",
      ["--vx-future"]: "#E9EDF2",
      ["--vx-futureText"]: "rgba(17,24,39,0.62)",

      ["--vx-h"]: "52px",
      ["--vx-arrow"]: "18px",
      ["--vx-downArrow"]: "7px",
      ["--vx-gap"]: "1px",
      ["--vx-radius"]: "16px",
    }),
    [theme]
  );

  const chipDisabled = !quoteId || loading || !canEdit || busy;

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const pager = mobilePagerRef.current;
    const stagePane = stagePaneRef.current;
    const actionsPane = actionsPaneRef.current;

    if (!root || !pager || !stagePane || !actionsPane) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    /*
     * Proven mobile-host resolution:
     * walk upward from the actual widget root and use the first ancestor whose
     * width still matches the widget and whose inline height is fixed.
     */
    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const naturalPaneHeight = (pane) => {
      if (!pane) return 0;

      const rectHeight = Math.ceil(
        pane.getBoundingClientRect().height || 0
      );

      const scrollHeight = Math.ceil(
        pane.scrollHeight || 0
      );

      return Math.max(rectHeight, scrollHeight);
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      /*
       * Clear our own previous sizing before measuring. Without this reset, the
       * previous --vx-mobile-pane-h becomes the next measurement input and the
       * widget behaves as though its mobile height were fixed.
       */
      pager.style.removeProperty("--vx-mobile-pane-h");
      pager.style.height = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const stageHeight = naturalPaneHeight(stagePane);
        const actionsHeight = naturalPaneHeight(actionsPane);

        const paneHeight = Math.ceil(
          Math.max(stageHeight, actionsHeight)
        );

        if (!Number.isFinite(paneHeight) || paneHeight <= 0) {
          return;
        }

        pager.style.setProperty(
          "--vx-mobile-pane-h",
          `${paneHeight}px`
        );

        /*
         * Pager is exactly one complete pane high. Pane 2 remains immediately
         * below Pane 1 and is reached by the native vertical scroll/snap.
         */
        pager.style.height = `${paneHeight}px`;

        root.style.height = "auto";
        root.style.minHeight = "0px";

        /*
         * Second frame allows pager sizing to settle before measuring the true
         * rendered widget/root geometry and WidgetWrapper offset.
         */
        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(stagePane);
      resizeObserver.observe(actionsPane);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.(
      "change",
      handleMediaChange
    );

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.(
        "change",
        handleMediaChange
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      restoreHost();

      pager.style.removeProperty("--vx-mobile-pane-h");
      pager.style.height = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    allowedNext.length,
    busy,
    canEdit,
    currentIdx,
    loading,
    quoteId,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      <style>{`
        .vx-bar {
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 10px;
          align-items: start;
          min-height: 0;
        }
        @media (max-width: 1100px) { .vx-bar { grid-template-columns: 1fr; } }

        .vx-track {
          background: rgba(0,0,0,0.02);
          border-radius: 16px;
          padding: 12px;
          border: 1px solid rgba(0,0,0,0.08);
          overflow: hidden;
        }

        .vx-stepsShell {
          background: var(--vx-barbg);
          border: 1px solid rgba(0,0,0,0.10);
          border-radius: var(--vx-radius);
          padding: 6px;
          overflow: hidden;
        }

        .vx-stepsWrap { overflow-x: auto; overflow-y: hidden; -webkit-overflow-scrolling: touch; }

        .vx-steps {
          display: flex;
          align-items: center;
          height: var(--vx-h);
          background: var(--vx-barbg);
          border-radius: var(--vx-radius);
          padding: 3px;
          gap: var(--vx-gap);
          min-width: 920px;
          overflow: hidden;
        }

        .vx-step {
          position: relative;
          flex: 1 1 0;
          height: calc(var(--vx-h) - 12px);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 950;
          font-size: 12px;
          letter-spacing: 0.2px;
          text-transform: uppercase;
          user-select: none;
          white-space: nowrap;

          clip-path: polygon(
            0% 0%,
            calc(100% - var(--vx-arrow)) 0%,
            100% 50%,
            calc(100% - var(--vx-arrow)) 100%,
            0% 100%,
            var(--vx-arrow) 50%
          );
          box-shadow: inset 0 0 0 1px rgba(17, 24, 39, 0.12);
        }

        .vx-step:first-child {
          clip-path: polygon(
            0% 0%,
            calc(100% - var(--vx-arrow)) 0%,
            100% 50%,
            calc(100% - var(--vx-arrow)) 100%,
            0% 100%
          );
          border-top-left-radius: var(--vx-radius);
          border-bottom-left-radius: var(--vx-radius);
        }

        .vx-step:last-child {
          clip-path: polygon(
            0% 0%,
            100% 0%,
            100% 100%,
            0% 100%,
            var(--vx-arrow) 50%
          );
          border-top-right-radius: var(--vx-radius);
          border-bottom-right-radius: var(--vx-radius);
        }

        .vx-complete { background: var(--vx-accent); color: #fff; }
        .vx-current { background: var(--vx-primary); color: #fff; }
        .vx-future { background: var(--vx-future); color: var(--vx-futureText); }

        .vx-step-status-accepted.vx-current { background: var(--vx-success); color: #fff; }
        .vx-step-status-rejected.vx-current,
        .vx-step-status-withdrawn.vx-current { background: var(--vx-danger); color: #fff; }
        .vx-step-status-expired.vx-current { background: var(--vx-muted); color: #fff; }
        .vx-step-status-sent.vx-current { background: var(--vx-info); color: #fff; }

        .vx-actions {
          border-radius: 16px;
          padding: 12px;
          border: 1px solid rgba(0,0,0,0.08);
          background: rgba(255,255,255,0.55);
          backdrop-filter: blur(2px);
          min-height: 0;
          display: grid;
          gap: 10px;
        }

        .vx-chipRow { display: flex; gap: 8px; flex-wrap: wrap; }

        .vx-chip {
          border: 0;
          border-radius: 999px;
          padding: 8px 10px;
          font-weight: 950;
          font-size: 12px;
          cursor: pointer;
          white-space: nowrap;
        }

        .vx-muted {
          font-size: 12px;
          opacity: 0.75;
          font-weight: 850;
        }

        /* Mobile: compact vertical lifecycle then vertical snap to actions */
        @media (max-width: 767px) {
          .vx-bar {
            display: block;
            height: var(--vx-mobile-pane-h, auto);
            max-height: none;
            overflow-x: hidden;
            overflow-y: auto;
            scroll-snap-type: y mandatory;
            scroll-behavior: smooth;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .vx-bar::-webkit-scrollbar {
            display: none;
          }

          .vx-track,
          .vx-actions {
            width: 100%;
            min-width: 0;
            min-height: var(--vx-mobile-pane-h, 0px);
            box-sizing: border-box;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-track {
            padding: 8px;
            overflow: visible;
          }

          .vx-stepsShell {
            padding: 4px;
            overflow: visible;
          }

          .vx-stepsWrap {
            overflow: visible;
          }

          .vx-steps {
            flex-direction: column;
            align-items: stretch;
            width: 100%;
            min-width: 0;
            height: auto;
            padding: 2px;
            gap: 2px;
            overflow: visible;
          }

          .vx-step {
            box-sizing: border-box;
            flex: 0 0 auto;
            width: 100%;
            height: 44px;
            min-height: 44px;
            padding: 9px 10px 11px;
            font-size: 10.5px;
            line-height: 13px;
            letter-spacing: 0.1px;
            white-space: normal;
            text-align: center;

            clip-path: polygon(
              0% 0%,
              50% var(--vx-downArrow),
              100% 0%,
              100% calc(100% - var(--vx-downArrow)),
              50% 100%,
              0% calc(100% - var(--vx-downArrow))
            );
          }

          .vx-step:first-child {
            padding-top: 7px;
            padding-bottom: 13px;

            clip-path: polygon(
              0% 0%,
              100% 0%,
              100% calc(100% - var(--vx-downArrow)),
              50% 100%,
              0% calc(100% - var(--vx-downArrow))
            );

            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            border-bottom-left-radius: 0;
          }

          .vx-step:last-child {
            padding-top: 13px;
            padding-bottom: 7px;

            clip-path: polygon(
              0% 0%,
              50% var(--vx-downArrow),
              100% 0%,
              100% 100%,
              0% 100%
            );

            border-top-right-radius: 0;
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
          }

          .vx-actions {
            align-content: start;
            gap: 8px;
            padding: 10px;
            border-radius: 12px;
          }

          .vx-chipRow {
            display: grid;
            grid-template-columns: 1fr;
            gap: 6px;
          }

          .vx-chip {
            width: 100%;
            min-width: 0;
            min-height: 44px;
            padding: 8px 10px !important;
            border-radius: 10px !important;
            font-size: 11px !important;
            white-space: normal;
          }

          .vx-muted {
            font-size: 11px;
            overflow-wrap: anywhere;
          }
        }
      `}</style>

      {tooltip.tooltipNode}

      <div ref={mobilePagerRef} className="vx-bar">
        <div ref={stagePaneRef} className="vx-track">
          <div className="vx-stepsShell">
            <div className="vx-stepsWrap">
              <div className="vx-steps">
                {QUOTE_STATUSES.map((s, i) => {
                  const st = stateByIdx[i] || "future";
                  const cls =
                    st === "complete"
                      ? `vx-step vx-complete vx-step-status-${s}`
                      : st === "current"
                      ? `vx-step vx-current vx-step-status-${s}`
                      : `vx-step vx-future vx-step-status-${s}`;

                  const tip = [
                    statusLabel(s),
                    STATUS_HELP[s] || "",
                    i < currentIdx ? "Completed status." : i === currentIdx ? "Current status." : "Upcoming status.",
                    quoteId ? `Quote #${quoteId}` : "No quote selected.",
                  ]
                    .filter(Boolean)
                    .join("\n");

                  return (
                    <div
                      key={s}
                      className={cls}
                      onMouseEnter={() => tooltip.bind.onMouseEnter(tip)}
                      onMouseLeave={tooltip.bind.onMouseLeave}
                      onMouseMove={tooltip.bind.onMouseMove}
                    >
                      {statusLabel(s)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className="break-words"
            style={{
              marginTop: 8,
              fontSize: 12,
              opacity: 0.75,
              fontWeight: 900,
              overflowWrap: "anywhere",
            }}
          >
            {quoteId ? `Selected: #${quoteId}` : "Select a quote to track status."}
          </div>
        </div>

        <div ref={actionsPaneRef} className="vx-actions">
          <div style={{ fontWeight: 950, fontSize: 12 }}>Available status changes</div>

          <div className="vx-chipRow">
            {allowedNext.length ? (
              allowedNext.map((s) => {
                const tip = !quoteId
                  ? "Select a quote first."
                  : loading
                  ? "Loading…"
                  : !canEdit
                  ? "View-only.\nYou do not have edit access."
                  : busy
                  ? "Working…"
                  : `Move quote to ${statusLabel(s)}.\nDB-governed transition.`;

                return (
                  <button
                    key={s}
                    type="button"
                    className={`vx-chip ${buttonClassForStatus(s)}`}
                    disabled={chipDisabled}
                    onClick={() => doTransition(s)}
                    onMouseEnter={() => tooltip.bind.onMouseEnter(tip)}
                    onMouseLeave={tooltip.bind.onMouseLeave}
                    onMouseMove={tooltip.bind.onMouseMove}
                    style={{
                      borderRadius: 999,
                      padding: "8px 10px",
                      fontWeight: 950,
                      fontSize: 12,
                      opacity: chipDisabled ? 0.55 : 1,
                    }}
                  >
                    {statusLabel(s)}
                  </button>
                );
              })
            ) : (
              <div className="vx-muted">{quoteId ? "No governed transitions available." : "Select a quote."}</div>
            )}
          </div>

          <div className="vx-muted">{canEdit ? "Editable" : "View-only"}</div>
        </div>
      </div>
    </div>
  );
}
